import { useState } from 'react'
import { 
  FlaskConical, 
  Clock,
  Zap,
  Shield,
  Star,
  CheckCircle,
  TrendingUp,
  Coins,
  Gem
} from 'lucide-react'
import { Button } from '../components/ui/button'
import GlassCard from '../components/GlassCard'
import Starfield from '../components/Starfield'
import { useGameState } from '../contexts/GameStateContext'

const ResearchCenter = () => {
  const { technologies, stats, startResearch, canAfford } = useGameState()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [isStartingResearch, setIsStartingResearch] = useState<string | null>(null)

  const researchQueue = technologies.filter(tech => tech.status === 'researching')

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'military': return Zap
      case 'defense': return Shield
      case 'economic': return TrendingUp
      case 'exploration': return Star
      default: return FlaskConical
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'military': return 'text-red-400 bg-red-600/20'
      case 'defense': return 'text-blue-400 bg-blue-600/20'
      case 'economic': return 'text-green-400 bg-green-600/20'
      case 'exploration': return 'text-purple-400 bg-purple-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-400 bg-green-600/20'
      case 'researching': return 'text-blue-400 bg-blue-600/20'
      case 'completed': return 'text-purple-400 bg-purple-600/20'
      case 'locked': return 'text-gray-400 bg-gray-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const filteredTechnologies = technologies.filter(tech => 
    selectedCategory === 'all' || tech.category === selectedCategory
  )

  const handleStartResearch = (techId: string) => {
    setIsStartingResearch(techId)
    try {
      // Update local state
      startResearch(techId)
    } catch (error) {
      console.error('Failed to start research:', error)
    } finally {
      setIsStartingResearch(null)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      <Starfield density={200} speed={0.5} />
      <div className="relative z-10 pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Header with Resources */}
          <GlassCard variant="premium" className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
                  <FlaskConical className="w-10 h-10 mr-3 text-purple-400" />
                  Research Center
                </h1>
                <p className="text-gray-400">Advance your civilization through technological innovation</p>
              </div>
              
              {/* Research Stats */}
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <span className="text-xs text-gray-400">RESEARCH POINTS</span>
                  <div className="flex items-center">
                    <Gem className="w-4 h-4 text-purple-400 mr-1" />
                    <span className="text-white font-bold">{stats.credits || 0}</span>
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-xs text-gray-400">COMPLETED</span>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-400 mr-1" />
                    <span className="text-white font-bold">
                      {technologies.filter(t => t.status === 'completed').length}
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-xs text-gray-400">IN PROGRESS</span>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-blue-400 mr-1" />
                    <span className="text-white font-bold">{researchQueue.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Research Filters */}
            <div className="xl:col-span-3">
              <div className="flex items-center space-x-4 mb-6">
                {['all', 'military', 'defense', 'economic', 'exploration'].map((category) => (
                  <Button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    variant={selectedCategory === category ? 'default' : 'secondary'}
                    className="capitalize"
                  >
                    {category === 'all' ? 'All Technologies' : category}
                  </Button>
                ))}
              </div>

              {/* Technologies Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredTechnologies.map((tech) => {
                  const CategoryIcon = getCategoryIcon(tech.category)
                  return (
                    <GlassCard key={tech.id} variant="default" className="hover:bg-white/10 transition-all duration-300">
                      <div className="p-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <CategoryIcon className="w-6 h-6 text-blue-400" />
                            <div>
                              <h3 className="text-lg font-bold text-white">{tech.name}</h3>
                              <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(tech.category)}`}>
                                {tech.category}
                              </span>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(tech.status)}`}>
                            {tech.status}
                          </span>
                        </div>

                        {/* Description */}
                        <p className="text-gray-300 text-sm mb-4">{tech.description}</p>

                        {/* Progress Bar (if researching) */}
                        {tech.status === 'researching' && (
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-gray-400">Progress</span>
                              <span className="text-blue-400">{tech.progress}%</span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${tech.progress}%` }}
                              ></div>
                            </div>
                            {tech.timeRemaining && (
                              <div className="text-xs text-gray-400 mt-1">
                                Time remaining: {tech.timeRemaining}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Prerequisites */}
                        {tech.prerequisites.length > 0 && (
                          <div className="mb-4">
                            <div className="text-xs text-gray-400 mb-2">Prerequisites:</div>
                            <div className="flex flex-wrap gap-1">
                              {tech.prerequisites.map((prereq, index) => (
                                <span key={index} className="px-2 py-1 bg-slate-700/50 text-xs text-gray-300 rounded">
                                  {prereq}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Cost and Duration */}
                        <div className="flex justify-between items-center mb-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Coins className="w-4 h-4 text-yellow-400" />
                            <span className="text-gray-400">
                              Cost: <span className="text-yellow-400">{tech.cost}</span>
                            </span>
                          </div>
                          <div className="text-gray-400">
                            Duration: <span className="text-blue-400">{tech.duration}m</span>
                          </div>
                        </div>

                        {/* Action Button */}
                        <Button 
                          className="w-full" 
                          disabled={
                            tech.status === 'researching' || 
                            tech.status === 'completed' || 
                            tech.status === 'locked' || 
                            !canAfford({ credits: tech.cost }) ||
                            isStartingResearch === tech.id
                          }
                          variant={tech.status === 'available' ? 'default' : 'secondary'}
                          onClick={() => tech.status === 'available' && handleStartResearch(tech.id)}
                        >
                          {isStartingResearch === tech.id && 'Starting Research...'}
                          {isStartingResearch !== tech.id && tech.status === 'available' && (canAfford({ credits: tech.cost }) ? 'Start Research' : 'Insufficient Credits')}
                          {isStartingResearch !== tech.id && tech.status === 'researching' && 'Researching...'}
                          {isStartingResearch !== tech.id && tech.status === 'completed' && 'Completed'}
                          {isStartingResearch !== tech.id && tech.status === 'locked' && 'Locked'}
                        </Button>
                      </div>
                    </GlassCard>
                  )
                })}
              </div>
            </div>

            {/* Research Queue Sidebar */}
            <div className="space-y-6">
              {/* Current Research */}
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                <h3 className="text-lg font-bold text-white mb-4">Research Queue</h3>
                
                {researchQueue.length > 0 ? (
                  <div className="space-y-3">
                    {researchQueue.map((item, index) => (
                      <div key={index} className={`p-3 rounded-lg ${index === 0 ? 'bg-blue-600/20 border border-blue-500/30' : 'bg-slate-700/30'}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-white text-sm font-medium">{item.name}</span>
                          <span className="text-xs text-gray-400">{item.timeRemaining}</span>
                        </div>
                        {index === 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-slate-700 rounded-full h-1">
                              <div className="bg-blue-500 h-1 rounded-full" style={{ width: '65%' }}></div>
                            </div>
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {index === 0 ? 'Currently researching' : `Position ${index + 1} in queue`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <FlaskConical className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No research in progress</p>
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-6 border border-purple-500/30">
                <h3 className="text-lg font-semibold text-white mb-4">Research Overview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Total Technologies</span>
                    <span className="text-white font-semibold">{technologies.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Completion Rate</span>
                    <span className="text-green-400 font-semibold">
                      {Math.round((technologies.filter(t => t.status === 'completed').length / technologies.length) * 100)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Research Speed</span>
                    <span className="text-blue-400 font-semibold">+25%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Queue Efficiency</span>
                    <span className="text-purple-400 font-semibold">Optimal</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResearchCenter